// eslint-disable-next-line import/extensions
import DOMPurify from 'dompurify'

// Ensure target="_blank" links have rel="noopener noreferrer"
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (
    node.tagName === 'A' &&
    node.getAttribute('target')?.toLowerCase() === '_blank'
  ) {
    const existingRel = node.getAttribute('rel') || ''
    const relValues = new Set(existingRel.split(/\s+/).filter(Boolean))
    relValues.add('noopener')
    relValues.add('noreferrer')
    node.setAttribute('rel', Array.from(relValues).join(' '))
  }
})

const PURIFY_CONFIG = Object.freeze({
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
})

const sanitizeText = (text) => DOMPurify.sanitize(text, PURIFY_CONFIG)

// Block-level tags whose boundaries should become whitespace so adjacent
// blocks don't run together when flattened to plain text (e.g. "...BELOW***"
// + "King County..." -> "...BELOW*** King County...").
const BLOCK_TAG_BOUNDARY =
  /<\/(p|div|li|ul|ol|h[1-6]|tr|blockquote)>|<br\s*\/?>/gi

// Flattens an (untrusted) HTML string to plain text. Sanitizes first to drop
// script/style content, then reads textContent to strip remaining tags and
// decode HTML entities. Use for compact previews where rendering markup isn't
// appropriate (e.g. clamped home-feed cards).
const toPlainText = (html) => {
  if (!html || typeof html !== 'string') return ''
  const spaced = html.replace(BLOCK_TAG_BOUNDARY, ' ')
  const doc = new DOMParser().parseFromString(sanitizeText(spaced), 'text/html')
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

// Recursively sanitizes all string values in a request body
const sanitizeRequestBody = (body) => {
  if (body == null) return body

  if (typeof body === 'string') {
    return sanitizeText(body)
  }

  if (Array.isArray(body)) {
    return body.map((item) => sanitizeRequestBody(item))
  }

  if (typeof body === 'object') {
    return Object.fromEntries(
      Object.entries(body).map(([key, value]) => [
        key,
        sanitizeRequestBody(value),
      ])
    )
  }

  return body
}

export { sanitizeText, sanitizeRequestBody, toPlainText }
